import httpClientClubEvents from '../api/httpClientClubEvents';

export const getAllEvents = async ({ pageNumber = 1, pageSize = 10, isActive, isCompleted } = {}) => {
  const response = await httpClientClubEvents.get('/api/clubevents/ClubEventManagement/GetAll', {
    params: { pageNumber, pageSize, isActive, isCompleted },
  });
  return response.data;
};

export const getEventById = async (id) => {
  const response = await httpClientClubEvents.get(`/api/clubevents/ClubEventManagement/GetById/${id}`);
  return response.data;
};

export const createEvent = async (payload) => {
  const response = await httpClientClubEvents.post('/api/clubevents/ClubEventManagement/Create', payload);
  return response.data;
};

export const createEventWithGallery = async ({ dto, galleryImages = [] }) => {
  const formData = new FormData();
  formData.append('dto', JSON.stringify(dto));
  (galleryImages || []).forEach((file) => {
    if (file instanceof File) formData.append('galleryImages', file, file.name);
  });
  const response = await httpClientClubEvents.post('/api/clubevents/ClubEventManagement/CreateWithGallery', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

export const updateEvent = async (payload) => {
  const response = await httpClientClubEvents.put('/api/clubevents/ClubEventManagement/Update', payload);
  return response.data;
};

export const deleteEvent = async (id) => {
  const response = await httpClientClubEvents.delete(`/api/clubevents/ClubEventManagement/Delete/${id}`);
  return response.data;
};

export const getParticipants = async (eventId) => {
  const response = await httpClientClubEvents.get(`/api/clubevents/ParticipantManagement/GetParticipants/${eventId}`);
  return response.data;
};

export const getParticipantSummary = async (eventId) => {
  const response = await httpClientClubEvents.get(`/api/clubevents/ParticipantManagement/GetSummary/${eventId}`);
  return response.data;
};

export const addParticipant = async (payload) => {
  // payload must be exactly: { EventId: Guid, PersonId: Guid, Type: number }
  const body = {
    EventId: payload?.EventId,
    PersonId: payload?.PersonId,
    Type: payload?.Type,
  };
  const response = await httpClientClubEvents.post('/api/clubevents/ParticipantManagement/AddParticipant', body);
  return response.data;
};

export const addGuest = async (payload) => {
  // payload: { EventId, HostPersonId, HostType, FirstName, LastName, PhoneNumber? }
  const body = {
    EventId: payload?.EventId,
    HostPersonId: payload?.HostPersonId,
    HostType: payload?.HostType,
    FirstName: payload?.FirstName,
    LastName: payload?.LastName,
    PhoneNumber: payload?.PhoneNumber || null,
  };
  const response = await httpClientClubEvents.post('/api/clubevents/ParticipantManagement/AddGuest/Guest', body);
  return response.data;
};

export const getGuestGroups = async (eventId) => {
  const response = await httpClientClubEvents.get(`/api/clubevents/ParticipantManagement/Guests/${eventId}`);
  return response.data;
};

export const getGuestsByPersonId = async (eventId, personId, personType) => {
  const response = await httpClientClubEvents.get(`/api/clubevents/ParticipantManagement/GetGuestsByPersonId/Guests/${eventId}/Person/${personId}`, {
    params: { personType },
  });
  return response.data;
};

export const updateGuest = async (payload) => {
  // payload: { EventId, GuestId, FirstName, LastName, PhoneNumber? }
  const body = {
    EventId: payload?.EventId,
    GuestId: payload?.GuestId,
    FirstName: payload?.FirstName,
    LastName: payload?.LastName,
    PhoneNumber: payload?.PhoneNumber || null,
  };
  const response = await httpClientClubEvents.put('/api/clubevents/ParticipantManagement/UpdateGuest/Guest', body);
  return response.data;
};

export const removeGuest = async (payload) => {
  // payload: { EventId, GuestId }
  const body = {
    EventId: payload?.EventId,
    GuestId: payload?.GuestId,
  };
  const response = await httpClientClubEvents.delete('/api/clubevents/ParticipantManagement/RemoveGuest/Guest', { data: body });
  return response.data;
};

export const updateGuestAttendance = async (payload) => {
  // payload: { EventId, GuestId, AttendanceStatus }
  const body = {
    EventId: payload?.EventId,
    GuestId: payload?.GuestId,
    AttendanceStatus: payload?.AttendanceStatus,
  };
  const response = await httpClientClubEvents.post('/api/clubevents/ParticipantManagement/UpdateGuestAttendance/Guest/Attendance', body);
  return response.data;
};

export const approveParticipant = async ({ EventId, ParticipantId, IsApproved }) => {
  const response = await httpClientClubEvents.post('/api/clubevents/ParticipantManagement/ApproveParticipant', { EventId, ParticipantId, IsApproved });
  return response.data;
};

export const removeParticipant = async ({ EventId, ParticipantId }) => {
  const response = await httpClientClubEvents.post('/api/clubevents/ParticipantManagement/RemoveParticipant', { EventId, ParticipantId });
  return response.data;
};

export const getCalendarEvents = async ({ start, end, includeInactive = false } = {}) => {
  const response = await httpClientClubEvents.get('/api/clubevents/ClubEventManagement/GetAllEvents/Calendar', {
    params: { start, end, includeInactive },
  });
  return response.data;
};

export const updateParticipantAttendance = async ({ EventId, ParticipantId, AttendanceStatus }) => {
  const response = await httpClientClubEvents.post('/api/clubevents/ParticipantManagement/UpdateAttendance', {
    EventId,
    ParticipantId,
    AttendanceStatus,
  });
  return response.data;
};

export const searchEvents = async ({ keyword, city, eventType, isActive, pageNumber = 1, pageSize = 10 } = {}) => {
  const response = await httpClientClubEvents.get('/api/clubevents/ClubEventManagement/Search', {
    params: { keyword, city, eventType, isActive, pageNumber, pageSize },
  });
  return response.data;
};

export const getParticipantStatsByMemberId = async (memberId, type) => {
  // type: 0 = Member (by-member), 1 = SubMember (by-submember)
  const typeSegment = type === 1 ? 'by-submember' : 'by-member';
  const response = await httpClientClubEvents.get(`/api/clubevents/ParticipantStats/GetByMemberId/${typeSegment}/${memberId}`);
  return response.data;
};

export const getTopParticipantsByAttendanceRate = async (topCount = 20) => {
  const response = await httpClientClubEvents.get('/api/clubevents/ParticipantStats/GetTopParticipantsByAttendanceRate/top-by-attendance-rate', {
    params: { topCount },
  });
  return response.data;
};

export const getAllParticipantStats = async (params = {}) => {
  const {
    pageNumber = 1,
    pageSize = 20,
    type = null,
    minAttendanceRate = null,
    maxAttendanceRate = null,
    minLifetimeAttendanceRate = null,
    maxLifetimeAttendanceRate = null,
    minAttendedEventsCount = null,
    minRegisteredEventsCount = null,
    searchKeyword = null,
    sortBy = null,
    memberId = null,
  } = params;
  
  const queryParams = {};
  if (pageNumber) queryParams.pageNumber = pageNumber;
  if (pageSize) queryParams.pageSize = pageSize;
  if (type !== null && type !== undefined) queryParams.type = type;
  if (minAttendanceRate !== null && minAttendanceRate !== undefined) queryParams.minAttendanceRate = minAttendanceRate;
  if (maxAttendanceRate !== null && maxAttendanceRate !== undefined) queryParams.maxAttendanceRate = maxAttendanceRate;
  if (minLifetimeAttendanceRate !== null && minLifetimeAttendanceRate !== undefined) queryParams.minLifetimeAttendanceRate = minLifetimeAttendanceRate;
  if (maxLifetimeAttendanceRate !== null && maxLifetimeAttendanceRate !== undefined) queryParams.maxLifetimeAttendanceRate = maxLifetimeAttendanceRate;
  if (minAttendedEventsCount !== null && minAttendedEventsCount !== undefined) queryParams.minAttendedEventsCount = minAttendedEventsCount;
  if (minRegisteredEventsCount !== null && minRegisteredEventsCount !== undefined) queryParams.minRegisteredEventsCount = minRegisteredEventsCount;
  if (searchKeyword) queryParams.searchKeyword = searchKeyword;
  if (sortBy !== null && sortBy !== undefined) queryParams.sortBy = sortBy;
  if (memberId) queryParams.memberId = memberId;

  const response = await httpClientClubEvents.get('/api/clubevents/ParticipantStats/GetAll', {
    params: queryParams,
  });
  return response.data;
};

export const getParticipantStatsById = async (id) => {
  const response = await httpClientClubEvents.get(`/api/clubevents/ParticipantStats/GetById/${id}`);
  return response.data;
};

export const recalculateParticipantStats = async (memberId, type) => {
  const response = await httpClientClubEvents.post(`/api/clubevents/ParticipantStats/RecalculateStats/${memberId}/recalculate`, null, {
    params: { type },
  });
  return response.data;
};

export const recalculateAllParticipantStats = async () => {
  const response = await httpClientClubEvents.post('/api/clubevents/ParticipantStats/RecalculateAllStats/recalculate-all');
  return response.data;
};

export const getEventAttendanceReport = async (eventId) => {
  const response = await httpClientClubEvents.get(`/api/clubevents/ParticipantManagement/GetReport/GetReport/${eventId}`);
  return response.data;
};

const parseContentDispositionFileName = (disposition, fallback) => {
  if (!disposition) return fallback;
  const match = disposition.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i);
  if (match?.[1]) {
    try {
      return decodeURIComponent(match[1]);
    } catch {
      return match[1];
    }
  }
  const legacyMatch = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(disposition);
  if (legacyMatch?.[1]) {
    return decodeURIComponent(legacyMatch[1].replace(/['"]/g, ''));
  }
  return fallback;
};

export const downloadEventAttendanceReportExcel = async (eventId) => {
  const response = await httpClientClubEvents.get(
    `/api/clubevents/ParticipantManagement/ReportExcel/ReportExcel/${eventId}`,
    { responseType: 'blob' },
  );

  const disposition = response.headers?.['content-disposition'] || response.headers?.['Content-Disposition'];
  const fileName = parseContentDispositionFileName(disposition, `etkinlik-katilim-${eventId}.csv`);

  return {
    blob: response.data,
    fileName,
    contentType: response.headers?.['content-type'] || 'text/csv;charset=utf-8',
  };
};

export default {
  getAllEvents,
  getEventById,
  createEvent,
  createEventWithGallery,
  updateEvent,
  deleteEvent,
  getParticipants,
  getParticipantSummary,
  addParticipant,
  addGuest,
  getGuestGroups,
  getGuestsByPersonId,
  updateGuest,
  removeGuest,
  updateGuestAttendance,
  approveParticipant,
  removeParticipant,
  updateParticipantAttendance,
  getCalendarEvents,
  searchEvents,
  getParticipantStatsByMemberId,
  getTopParticipantsByAttendanceRate,
  getAllParticipantStats,
  getParticipantStatsById,
  recalculateParticipantStats,
  recalculateAllParticipantStats,
  getEventAttendanceReport,
  downloadEventAttendanceReportExcel,
};


